import React from "react";
import { PricingTierItemInterface, ProductItemInterface } from "@/assets/interfaces";
import { enumToHumanFriendly, getActualPrice } from "@/assets/js/utils/utils";
import { getVariantTypesAndCounts } from "@/assets/js/utils/product-options";
import CartContext from "@/contexts/cart-context";
import CartContextV2 from "@/contexts/cart-context-v2";

interface PricingTiersProps {
  item: ProductItemInterface;
}

const PricingTiers = ({ item }: PricingTiersProps) => {
  const { formatAsCurrency } = CartContextV2.useContainer();
  if (!item?.tiered_pricing || !item?.tiered_pricing?.tiers.length || !item?.tiered_pricing?.active) return null;

  const tiers = item?.tiered_pricing?.tiers ?? [];

  return (
    <div>
      <h5 className="text-black font-display font-bold text-sm md:text-1sm max-w-full whitespace-nowrap overflow-ellipsis my-2.5">
        Discounted Prices for Bulk Purchases
      </h5>
      <div className="w-full flex flex-col gap-2.5">
        {/* <p className="text-sm text-dark mb-1">Get discounted prices when you make a bulk purchase.</p> */}
        <div className="flex flex-col items-start space-y-2.5">
          <TierItem
            quantity={`1 - ${item?.tiered_pricing?.tiers[0].minimum_quantity - 1}`}
            price_per_item={formatAsCurrency(getActualPrice(item))}
          />

          {tiers.map((tier, index) => (
            <TierItem
              key={tier.minimum_quantity}
              quantity={`${tier.minimum_quantity} - ${
                tiers[index + 1] ? tiers[index + 1].minimum_quantity - 1 : "Unlimited"
              }`}
              price_per_item={formatAsCurrency(getActualPrice(item) * (1 - tier.discount_percentage / 100))}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingTiers;

interface TierItemProps {
  quantity: string;
  price_per_item: string;
}

const TierItem = ({ quantity, price_per_item }: TierItemProps) => {
  return (
    <div className="py-2.5 px-3 bg-grey-fields-100 rounded-30 inline-flex items-center justify-center space-x-2">
      <div className="flex items-center">
        {/* prettier-ignore */}
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" className="text-black-placeholder">
          <path d="M7.86091 8.41999C7.50091 8.41999 7.13425 8.35332 6.84758 8.22666L2.91424 6.47999C1.91424 6.03332 1.76758 5.43332 1.76758 5.10666C1.76758 4.77999 1.91424 4.17999 2.91424 3.73332L6.84758 1.98666C7.42758 1.72666 8.30091 1.72666 8.88091 1.98666L12.8209 3.73332C13.8142 4.17332 13.9676 4.77999 13.9676 5.10666C13.9676 5.43332 13.8209 6.03332 12.8209 6.47999L8.88091 8.22666C8.58758 8.35999 8.22758 8.41999 7.86091 8.41999ZM7.86091 2.79332C7.63424 2.79332 7.41424 2.82666 7.25424 2.89999L3.32091 4.64666C2.91424 4.83332 2.76758 5.01999 2.76758 5.10666C2.76758 5.19332 2.91424 5.38666 3.31424 5.56666L7.24758 7.31332C7.56758 7.45332 8.14758 7.45332 8.46758 7.31332L12.4076 5.56666C12.8142 5.38666 12.9609 5.19332 12.9609 5.10666C12.9609 5.01999 12.8142 4.82666 12.4076 4.64666L8.47424 2.89999C8.31424 2.83332 8.08758 2.79332 7.86091 2.79332Z" fill="currentColor"/>
          <path d="M8 11.8933C7.74667 11.8933 7.49333 11.84 7.25333 11.7333L2.72667 9.72001C2.04 9.42001 1.5 8.58668 1.5 7.83334C1.5 7.56001 1.72667 7.33334 2 7.33334C2.27333 7.33334 2.5 7.56001 2.5 7.83334C2.5 8.20001 2.8 8.66001 3.13333 8.81334L7.66 10.8267C7.87333 10.92 8.12 10.92 8.34 10.8267L12.8667 8.81334C13.2 8.66668 13.5 8.20001 13.5 7.83334C13.5 7.56001 13.7267 7.33334 14 7.33334C14.2733 7.33334 14.5 7.56001 14.5 7.83334C14.5 8.58668 13.96 9.42001 13.2733 9.72668L8.74667 11.74C8.50667 11.84 8.25333 11.8933 8 11.8933Z" fill="currentColor"/>
          <path d="M8 15.2266C7.74667 15.2266 7.49333 15.1733 7.25333 15.0666L2.72667 13.0533C1.98 12.72 1.5 11.98 1.5 11.16C1.5 10.8866 1.72667 10.66 2 10.66C2.27333 10.66 2.5 10.8933 2.5 11.1666C2.5 11.5866 2.74667 11.9733 3.13333 12.1466L7.66 14.16C7.87333 14.2533 8.12 14.2533 8.34 14.16L12.8667 12.1466C13.2533 11.9733 13.5 11.5933 13.5 11.1666C13.5 10.8933 13.7267 10.6666 14 10.6666C14.2733 10.6666 14.5 10.8933 14.5 11.1666C14.5 11.9866 14.02 12.7266 13.2733 13.06L8.74667 15.0733C8.50667 15.1733 8.25333 15.2266 8 15.2266Z" fill="currentColor"/>
        </svg>
        <span className="text-black-muted font-medium inline-block ml-1 text-xs">{quantity}</span>
      </div>
      <svg width="6" height="7" viewBox="0 0 6 7" fill="none" className="text-grey-subtext">
        <path d="M3 0.5L6 3.5L3 6.5L0 3.5L3 0.5Z" fill="currentColor" />
      </svg>
      <span className="inline-block font-semibold text-primary-500 text-xs">{price_per_item}</span>
    </div>
  );
};
